import { useState, useEffect } from "react"
import { APP_VERSION } from "@/lib/version"
import {
  Sidebar, SidebarContent, SidebarFooter, SidebarGroup, SidebarGroupLabel,
  SidebarHeader, SidebarMenu, SidebarMenuAction, SidebarMenuButton, SidebarMenuItem,
  SidebarMenuSub, SidebarMenuSubButton, SidebarMenuSubItem,
} from "@/components/ui/sidebar"
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  Settings, ChevronsUpDown, LogOut, BadgeCheck, Plus, Layers, ChevronRight, CheckCircle2, Database,
} from "lucide-react"
import type { Job } from "@/App"
import { catalogueApi, type CatalogueDTO } from "@/lib/api"

const STATUS_COLOR: Record<Job['status'], string> = {
  processing:    '#818cf8',
  batching:      '#a78bfa',
  paused:        '#facc15',
  queued:        '#60a5fa',
  not_started:   '#94a3b8',
  not_configured:'#f97316',
  error:         '#f43f5e',
  partial:       '#f59e0b',
  cancelled:     '#94a3b8',
  done:          '#34d399',
}

function JobCircle({ job }: { job: Job }) {
  const r = 7
  const circ = 2 * Math.PI * r
  const color = STATUS_COLOR[job.status]

  if (job.status === 'processing') {
    return (
      <svg width="18" height="18" viewBox="0 0 18 18" className="shrink-0 animate-spin">
        <circle cx="9" cy="9" r={r} fill="none" stroke="currentColor" strokeWidth="2" className="text-border" />
        <circle cx="9" cy="9" r={r} fill="none" stroke={color} strokeWidth="2"
          strokeDasharray={`${circ * 0.75} ${circ * 0.25}`}
          strokeLinecap="round" />
        <circle cx="9" cy="9" r="3.5" fill={color} />
      </svg>
    )
  }

  // status is the truth; DB progress can be stale on jobs finished before the worker owned it
  const pct = job.status === 'done' ? 100 : job.progress ?? 0
  const dash = (pct / 100) * circ

  return (
    <svg width="18" height="18" viewBox="0 0 18 18" className="shrink-0" style={{ transform: 'rotate(-90deg)' }}>
      <circle cx="9" cy="9" r={r} fill="none" stroke="currentColor" strokeWidth="2" className="text-border" />
      <circle cx="9" cy="9" r={r} fill="none" stroke={color} strokeWidth="2"
        strokeDasharray={`${dash} ${circ}`}
        strokeLinecap="round" />
      <circle cx="9" cy="9" r="3.5" fill={color} />
    </svg>
  )
}

export function CliSidebar({ active, onNav, onLogout, onNewBatch, jobs, selectedJobId, onSelectJob, userEmail, tenantName, onNavCatalogue, enabledModules, credits }: {
  active: string
  onNav: (id: string) => void
  onLogout: () => void
  onNewBatch?: () => void
  jobs?: Job[]
  selectedJobId?: string | null
  onSelectJob?: (id: string) => void
  userEmail?: string
  tenantName?: string
  onNavCatalogue?: (catId: number) => void
  enabledModules?: string[]
  credits?: { budget: number; used: number } | null
}) {
  const [jobsOpen, setJobsOpen] = useState(true)
  const [doneOpen, setDoneOpen] = useState(true)
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [dataOpen, setDataOpen] = useState(false)
  const [catalogues, setCatalogues] = useState<CatalogueDTO[]>([])

  useEffect(() => {
    if (enabledModules?.includes('catalogue')) {
      catalogueApi.list().then(setCatalogues).catch(() => {})
    }
  }, [enabledModules])

  const activeJobs  = jobs?.filter(j => j.status !== 'done') ?? []
  const doneJobs    = jobs?.filter(j => j.status === 'done') ?? []
  const errorCount  = jobs?.filter(j => j.status === 'error').length ?? 0

  const SETTINGS_SECTIONS = [
    { id: 'settings-api',     label: 'API Keys' },
    { id: 'settings-cloud',   label: 'Cloud Storage' },
    { id: 'settings-sku',     label: 'Custom Data' },
    { id: 'settings-storage', label: 'Storage' },
  ]

  const scrollToSection = () => {
    onNav('settings')
  }
  return (
    <Sidebar collapsible="icon">
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton size="lg">
              <div className="flex h-8 w-8 items-center justify-center rounded-md bg-foreground flex-shrink-0">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                  <rect x="3" y="3" width="7" height="7" rx="1"/>
                  <rect x="14" y="3" width="7" height="7" rx="1"/>
                  <rect x="3" y="14" width="7" height="7" rx="1"/>
                  <rect x="14" y="14" width="7" height="7" rx="1"/>
                </svg>
              </div>
              <div className="flex flex-col text-left leading-tight">
                <span className="text-sm font-semibold truncate">TwinModel</span>
                <span className="text-[10px] text-muted-foreground truncate">{APP_VERSION}</span>
              </div>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>

      <SidebarContent>
        <SidebarGroup>
          <SidebarMenu>
            <SidebarMenuItem>
              <SidebarMenuButton tooltip="New Batch" onClick={onNewBatch} className="font-semibold">
                <Plus /><span>New Batch</span>
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarGroup>
        <SidebarGroup>
          <SidebarGroupLabel>General</SidebarGroupLabel>
          <SidebarMenu>
            <SidebarMenuItem>
              <SidebarMenuButton tooltip="Jobs" isActive={active === 'batches' && !selectedJobId} onClick={() => { onNav('batches'); onSelectJob?.('') }}>
                <Layers /><span>Jobs</span>
                <div className="ml-auto flex items-center gap-1.5">
                  {errorCount > 0 && (
                    <span className="flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-semibold text-white tabular-nums">
                      {errorCount}
                    </span>
                  )}
                  <span className="text-[10px] text-muted-foreground tabular-nums">{activeJobs.length}</span>
                </div>
              </SidebarMenuButton>
              {activeJobs.length > 0 && (
                <SidebarMenuAction onClick={() => setJobsOpen(v => !v)} className="transition-transform duration-150" style={{ transform: jobsOpen ? 'rotate(90deg)' : 'rotate(0deg)' }}>
                  <ChevronRight className="size-3.5" />
                </SidebarMenuAction>
              )}
              {jobsOpen && activeJobs.length > 0 && (
                <SidebarMenuSub>
                  {activeJobs.map(job => (
                    <SidebarMenuSubItem key={job.id}>
                      <SidebarMenuSubButton
                        isActive={active === 'batches' && selectedJobId === job.id}
                        onClick={() => { onNav('batches'); onSelectJob?.(job.id) }}
                        className="gap-2"
                      >
                        <JobCircle job={job} />
                        <span className="truncate">{job.name}</span>
                      </SidebarMenuSubButton>
                    </SidebarMenuSubItem>
                  ))}
                </SidebarMenuSub>
              )}
            </SidebarMenuItem>

            {doneJobs.length > 0 && (
              <SidebarMenuItem>
                <SidebarMenuButton tooltip="Finished" isActive={active === 'batches-done' && !selectedJobId} onClick={() => { onNav('batches-done'); onSelectJob?.(''); setDoneOpen(true) }}>
                  <CheckCircle2 /><span>Finished</span>
                  <span className="ml-auto text-[10px] text-muted-foreground tabular-nums">{doneJobs.length}</span>
                </SidebarMenuButton>
                <SidebarMenuAction onClick={() => setDoneOpen(v => !v)} className="transition-transform duration-150" style={{ transform: doneOpen ? 'rotate(90deg)' : 'rotate(0deg)' }}>
                  <ChevronRight className="size-3.5" />
                </SidebarMenuAction>
                {doneOpen && (
                  <SidebarMenuSub>
                    {doneJobs.map(job => (
                      <SidebarMenuSubItem key={job.id}>
                        <SidebarMenuSubButton
                          isActive={active === 'batches' && selectedJobId === job.id}
                          onClick={() => { onNav('batches'); onSelectJob?.(job.id) }}
                          className="gap-2"
                        >
                          <JobCircle job={job} />
                          <span className="truncate">{job.name}</span>
                        </SidebarMenuSubButton>
                      </SidebarMenuSubItem>
                    ))}
                  </SidebarMenuSub>
                )}
              </SidebarMenuItem>
            )}
          </SidebarMenu>
        </SidebarGroup>
        {enabledModules?.includes('catalogue') && (
        <SidebarGroup>
          <SidebarGroupLabel>Outils</SidebarGroupLabel>
          <SidebarMenu>
            <SidebarMenuItem>
              <SidebarMenuButton tooltip="Data" isActive={active === 'catalogue'} onClick={() => onNav('catalogue')}>
                <Database />
                <span>Data</span>
                {catalogues.length > 0 && (
                  <span className="ml-auto text-[10px] text-muted-foreground tabular-nums">{catalogues.length}</span>
                )}
              </SidebarMenuButton>
              {catalogues.length > 0 && (
                <SidebarMenuAction onClick={() => setDataOpen(v => !v)} className="transition-transform duration-150" style={{ transform: dataOpen ? 'rotate(90deg)' : 'rotate(0deg)' }}>
                  <ChevronRight className="size-3.5" />
                </SidebarMenuAction>
              )}
              {dataOpen && catalogues.length > 0 && (
                <SidebarMenuSub>
                  {catalogues.map(cat => (
                    <SidebarMenuSubItem key={cat.id}>
                      <SidebarMenuSubButton onClick={() => onNavCatalogue ? onNavCatalogue(cat.id) : onNav('catalogue')} className="gap-2">
                        <span className="truncate">{cat.name.replace(/_/g, ' ')}</span>
                        <span className="ml-auto text-[10px] text-muted-foreground tabular-nums shrink-0">{cat.product_count}</span>
                      </SidebarMenuSubButton>
                    </SidebarMenuSubItem>
                  ))}
                </SidebarMenuSub>
              )}
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarGroup>
        )}
        <SidebarGroup>
          <SidebarGroupLabel>System</SidebarGroupLabel>
          <SidebarMenu>
            <SidebarMenuItem>
              <SidebarMenuButton tooltip="Settings" isActive={active === 'settings'} onClick={() => onNav('settings')}>
                <Settings /><span>Settings</span>
              </SidebarMenuButton>
              <SidebarMenuAction onClick={() => setSettingsOpen(v => !v)} className="transition-transform duration-150" style={{ transform: settingsOpen ? 'rotate(90deg)' : 'rotate(0deg)' }}>
                <ChevronRight className="size-3.5" />
              </SidebarMenuAction>
              {settingsOpen && (
                <SidebarMenuSub>
                  {SETTINGS_SECTIONS.map(s => (
                    <SidebarMenuSubItem key={s.id}>
                      <SidebarMenuSubButton onClick={() => scrollToSection()}>
                        <span>{s.label}</span>
                      </SidebarMenuSubButton>
                    </SidebarMenuSubItem>
                  ))}
                </SidebarMenuSub>
              )}
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter>
        {credits && (
          <div className="px-3 py-2 border-t">
            <div className="flex items-center justify-between mb-1">
              <span className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide">Credits</span>
              <span className="text-[10px] font-semibold tabular-nums">
                {Math.max(0, credits.budget - credits.used).toFixed(1)} / {credits.budget.toFixed(0)}
              </span>
            </div>
            <div className="h-1.5 rounded-full bg-muted overflow-hidden">
              <div
                className="h-full rounded-full bg-foreground transition-all"
                style={{ width: `${Math.min(100, (credits.used / credits.budget) * 100).toFixed(1)}%` }}
              />
            </div>
          </div>
        )}
        <SidebarMenu>
          <SidebarMenuItem>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <SidebarMenuButton size="lg" className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground">
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-foreground text-[11px] font-semibold text-background flex-shrink-0">
                    {(tenantName ?? userEmail ?? 'U').charAt(0).toUpperCase()}
                  </div>
                  <div className="flex flex-col text-left text-sm leading-tight">
                    <span className="font-medium truncate">{tenantName || userEmail || '—'}</span>
                    <span className="text-[10px] text-muted-foreground truncate">{userEmail || ''}</span>
                  </div>
                  <ChevronsUpDown className="ml-auto size-4" />
                </SidebarMenuButton>
              </DropdownMenuTrigger>
              <DropdownMenuContent side="top" align="end" className="w-56 mb-1">
                <DropdownMenuLabel className="font-normal">
                  <div className="flex flex-col gap-0.5">
                    <span className="text-sm font-medium">{tenantName || userEmail || '—'}</span>
                    <span className="text-xs text-muted-foreground">{userEmail || ''}</span>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem><BadgeCheck className="mr-2 size-4" />Feedback</DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem className="text-destructive focus:text-destructive" onClick={onLogout}>
                  <LogOut className="mr-2 size-4" />Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  )
}
